import {
  Fragment,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import dayjs from 'dayjs';
import { Pencil, Trash2 } from 'lucide-react';

import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import FormRadioField from 'components/IAP/formFields/FormRadioField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import FormTextField from 'components/IAP/formFields/FormTextField';
import {
  ResourceRequest,
  ResourceRequestItem,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { Ics204CardSection } from 'components/IAP/planningView/widgets/AssignmentList/ics204BoxLabels';
import { PrintedValue } from 'components/IAP/planningView/widgets/icsBoxPrimitives';
import { NULL_VALUE } from 'constants/displayConstants';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFormContext, useWatch } from 'react-hook-form';

import { routeLabelForSlot } from './ics213rrRoute';
import type { Ics213RrEditingSection } from './ics213rrSections';

import { CustomResourcesDropdown } from 'components/MapPage/CorporateViewTabs/ResourceTab/CustomResourcesDropdown';

import { itemDescription, normalize213rrPriority } from './ics213rrPriority';

export type Ics213RrMemberPosition = {
  positionId: string;
  positionName: string;
};

type Ics213RrFormBodyProps = {
  mode: 'create' | 'view';
  request?: ResourceRequest | null;
  items: ResourceRequestItem[];
  setItems?: Dispatch<SetStateAction<ResourceRequestItem[]>>;
  logisticsItems?: ResourceRequestItem[][];
  setLogisticsItems?: Dispatch<SetStateAction<ResourceRequestItem[][]>>;
  memberPositions: Ics213RrMemberPosition[];
  reqCount?: Record<string, number>;
  /** Create mode: full-form editing. View mode: prefer `rowsEditable` + section edit. */
  editing?: boolean;
  locked?: boolean;
  /** View mode: show box-4 Add Row / row actions when unlocked. */
  rowsEditable?: boolean;
  /**
   * View mode: edit g/h/i Order # / ETA / Cost (LSC) on each box-4 row while
   * Logistics (box 13) is the open sign slot.
   */
  logisticsColsEditable?: boolean;
  onLogisticsColsPatch?: (
    index: number,
    patch: Pick<ResourceRequestItem, 'order_number' | 'eta' | 'cost'>
  ) => void;
  addItemSlot?: ReactNode;
  onEditItem?: (index: number) => void;
  addingItem?: boolean;
  editingItemIndex?: number | null;
  renderItemEditor?: (args: {
    item?: ResourceRequestItem;
    editIndex: number | null;
    logisticsCells: ReactNode;
  }) => ReactNode;
  /** Create mode can override boxes 2 and 3. */
  headerEditable?: boolean;
  /** View mode: ICS-204-style per-box edit. */
  editingSection?: Ics213RrEditingSection | null;
  onSectionActivate?: (section: Ics213RrEditingSection) => void;
  canEditSection?: (section: Ics213RrEditingSection) => boolean;
  renderSectionEditor?: (section: Ics213RrEditingSection) => ReactNode;
  /** Incomplete slot ids that show Click to Sign (any-order). */
  signSlotIds?: readonly string[];
  onSignSlot?: (slotId: string) => void;
  signPending?: boolean;
  /** Incomplete slot ids that show Send for review. */
  routeSlotIds?: readonly string[];
  onRouteSlot?: (slotId: string) => void;
  /** Incomplete slot ids that show Reject (choice vs Sign; never box 6). */
  rejectSlotIds?: readonly string[];
  onRejectSlot?: (slotId: string) => void;
  rejectPending?: boolean;
  /** Unlock Transfer on attached chips when the tip is fully signed. */
  canTransfer?: boolean;
  /** Create mode: Attach cell for a saved row (defaults to the null value). */
  renderAttachCell?: (item: ResourceRequestItem, index: number) => ReactNode;
  /** Hides a row's remove button when it returns false. */
  canRemoveItem?: (item: ResourceRequestItem) => boolean;
  /**
   * Lets any position fill i. Cost in the row editor (Linked ICS 213RR
   * prefill).
   */
  costEditable?: boolean;
};

function RequiredMark() {
  return <span aria-hidden="true"> *</span>;
}

/** Local datetime-local value. `new Date()` stays in this module helper. */
export function toDatetimeLocalValue(value?: string | Date | null) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const hasPosition = (memberPositions: Ics213RrMemberPosition[], name: string) =>
  memberPositions.some((pos) => pos.positionName === name);

const sectionDisabled = (disabled: boolean) =>
  cn(disabled && 'tw:pointer-events-none tw:cursor-not-allowed tw:opacity-50');

function FormBand({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-[7rem_minmax(0,1fr)]">
      <div className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-border tw:bg-muted tw:px-2 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:tracking-wide tw:text-foreground tw:uppercase">
        {label}
      </div>
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">{children}</div>
    </div>
  );
}

function yesNo(value: boolean | undefined | null) {
  return value ? 'Yes' : 'No';
}

/** Renders inside AddRequestedResource's FormProvider so LSC fields save with the row. */
function RowLogisticsEditCells({
  index,
  item,
  request,
  editable,
  costEditable = false,
}: {
  index: number;
  item?: ResourceRequestItem;
  request?: ResourceRequest | null;
  editable: boolean;
  /** Only i. Cost is editable (Linked ICS 213RR prefill, any position). */
  costEditable?: boolean;
}) {
  const { register } = useFormContext<ResourceRequestItem>();
  const orderNumber = useWatch({ name: 'order_number' });
  const eta = useWatch({ name: 'eta' });
  const cost = useWatch({ name: 'cost' });

  const costInput = (
    <input
      className="tw:w-20 tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-1 tw:py-0.5 tw:text-sm"
      aria-label={`Cost for item ${index + 1}`}
      type="number"
      min={0}
      {...register('cost', { valueAsNumber: true })}
    />
  );

  if (!editable && costEditable) {
    return (
      <>
        <td className="tw:px-1 tw:py-1">
          {orderNumber || item?.order_number || NULL_VALUE}
        </td>
        <td className="tw:px-1 tw:py-1">{eta || item?.eta || NULL_VALUE}</td>
        <td className="tw:px-1 tw:py-1">{costInput}</td>
      </>
    );
  }

  if (!editable) {
    return (
      <>
        <td className="tw:px-1 tw:py-1">
          {orderNumber ||
            item?.order_number ||
            request?.logistics_order_number ||
            NULL_VALUE}
        </td>
        <td className="tw:px-1 tw:py-1">
          {eta ||
            item?.eta ||
            (request?.logistics_eta
              ? dayjs(request.logistics_eta).format('MM/DD/YYYY HH:mm')
              : NULL_VALUE)}
        </td>
        <td className="tw:px-1 tw:py-1">
          {cost ?? item?.cost ?? request?.logistics_cost ?? NULL_VALUE}
        </td>
      </>
    );
  }

  return (
    <>
      <td className="tw:px-1 tw:py-1">
        <input
          className="tw:w-24 tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-1 tw:py-0.5 tw:text-sm"
          aria-label={`Order number for item ${index + 1}`}
          {...register('order_number')}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <input
          className="tw:w-36 tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-1 tw:py-0.5 tw:text-sm"
          aria-label={`ETA for item ${index + 1}`}
          {...register('eta')}
        />
      </td>
      <td className="tw:px-1 tw:py-1">{costInput}</td>
    </>
  );
}

/**
 * Standalone LSC columns for a view-mode row (no AddRequestedResource form).
 * Commits Order # / ETA / Cost when the user leaves a field.
 */
function StandaloneLogisticsColsEditor({
  index,
  item,
  onCommit,
}: {
  index: number;
  item: ResourceRequestItem;
  onCommit: (
    patch: Pick<ResourceRequestItem, 'order_number' | 'eta' | 'cost'>
  ) => void;
}) {
  const [orderNumber, setOrderNumber] = useState(item.order_number ?? '');
  const [eta, setEta] = useState(item.eta ? toDatetimeLocalValue(item.eta) : '');
  const [cost, setCost] = useState(
    item.cost === undefined || item.cost === null ? '' : String(item.cost)
  );

  useEffect(() => {
    setOrderNumber(item.order_number ?? '');
    setEta(item.eta ? toDatetimeLocalValue(item.eta) : '');
    setCost(
      item.cost === undefined || item.cost === null ? '' : String(item.cost)
    );
  }, [item.order_number, item.eta, item.cost]);

  const commit = () => {
    const parsedCost =
      cost.trim() === '' || Number.isNaN(Number(cost))
        ? undefined
        : Number(cost);
    const nextEta = eta.trim() ? new Date(eta).toISOString() : undefined;
    onCommit({
      order_number: orderNumber.trim() || undefined,
      eta: nextEta,
      cost: parsedCost,
    });
  };

  return (
    <>
      <td className="tw:px-1 tw:py-1">
        <Input
          className="tw:h-8 tw:w-24"
          aria-label={`Order number for item ${index + 1}`}
          value={orderNumber}
          onChange={(event) => setOrderNumber(event.target.value)}
          onBlur={commit}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <Input
          className="tw:h-8 tw:w-40"
          type="datetime-local"
          aria-label={`ETA for item ${index + 1}`}
          value={eta}
          onChange={(event) => setEta(event.target.value)}
          onBlur={commit}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <Input
          className="tw:h-8 tw:w-20"
          type="number"
          aria-label={`Cost for item ${index + 1}`}
          value={cost}
          onChange={(event) => setCost(event.target.value)}
          onBlur={commit}
        />
      </td>
    </>
  );
}

export function Ics213RrFormBody({
  mode,
  request,
  items,
  setItems,
  logisticsItems = [],
  setLogisticsItems,
  memberPositions,
  reqCount,
  editing = false,
  locked = false,
  rowsEditable = false,
  logisticsColsEditable = false,
  onLogisticsColsPatch,
  addItemSlot,
  onEditItem,
  addingItem = false,
  editingItemIndex = null,
  renderItemEditor,
  headerEditable = false,
  editingSection = null,
  onSectionActivate,
  canEditSection,
  renderSectionEditor,
  signSlotIds = [],
  onSignSlot,
  signPending = false,
  routeSlotIds = [],
  onRouteSlot,
  rejectSlotIds = [],
  onRejectSlot,
  rejectPending = false,
  canTransfer = false,
  renderAttachCell,
  canRemoveItem,
  costEditable = false,
}: Ics213RrFormBodyProps) {
  const incident = useIncident();
  const { register } = useFormContext();
  const isCreate = mode === 'create';
  const isResl = hasPosition(memberPositions, 'Resource Unit Leader');
  const isLogs = hasPosition(memberPositions, 'Logistics Section Chief');
  const isFinance = hasPosition(memberPositions, 'Finance Section Chief');

  const laterBoxesLocked = isCreate || locked;
  const reslDisabled = laterBoxesLocked || !isResl;
  const logsDisabled = laterBoxesLocked || !isLogs;
  const financeDisabled = laterBoxesLocked || !isFinance;

  const showRowActions = isCreate || rowsEditable || editing;
  const incidentName = incident.data?.name ?? '';
  const dateTime = request?.timestamp
    ? dayjs(request.timestamp).format('MM/DD/YYYY HH:mm')
    : '';

  const routeButton = (slotId: string) => {
    if (!routeSlotIds.includes(slotId) || !onRouteSlot) return null;
    const label = routeLabelForSlot(slotId);
    if (!label) return null;
    return (
      <Button type="button" size="sm" onClick={() => onRouteSlot(slotId)}>
        {label}
      </Button>
    );
  };

  const rejectButton = (slotId: string) => {
    if (!rejectSlotIds.includes(slotId) || !onRejectSlot) return null;
    return (
      <Button
        type="button"
        size="sm"
        variant="destructive"
        disabled={rejectPending}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onRejectSlot(slotId);
        }}
      >
        Reject
      </Button>
    );
  };

  const signButton = (slotId: string) => {
    if (!signSlotIds.includes(slotId) || !onSignSlot) return null;
    return (
      <Button
        type="button"
        size="sm"
        disabled={signPending}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onSignSlot(slotId);
        }}
      >
        Click to sign
      </Button>
    );
  };

  /** Route → Reject → Sign for this signature box (any order; omit missing pieces). */
  const slotActions = (slotId: string) => {
    const route = routeButton(slotId);
    const reject = rejectButton(slotId);
    const sign = signButton(slotId);
    if (!route && !reject && !sign) return null;
    return (
      <div className="tw:mt-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {route}
        {reject}
        {sign}
      </div>
    );
  };

  const sectionActivateProps = (section: Ics213RrEditingSection) => {
    const can = !isCreate && !!canEditSection?.(section);
    return {
      isEditing: editingSection === section,
      onSectionActivate: can ? () => onSectionActivate?.(section) : undefined,
    };
  };

  const sectionBody = (
    section: Ics213RrEditingSection,
    view: ReactNode
  ): ReactNode => {
    if (!isCreate && editingSection === section && renderSectionEditor) {
      return renderSectionEditor(section);
    }
    return view;
  };

  const logisticsViewCells = (item: ResourceRequestItem) => (
    <>
      <td className="tw:px-1 tw:py-1">
        {item.order_number || request?.logistics_order_number || NULL_VALUE}
      </td>
      <td className="tw:px-1 tw:py-1">
        {item.eta ||
          (request?.logistics_eta
            ? dayjs(request.logistics_eta).format('MM/DD/YYYY HH:mm')
            : NULL_VALUE)}
      </td>
      <td className="tw:px-1 tw:py-1">
        {item.cost ?? request?.logistics_cost ?? NULL_VALUE}
      </td>
    </>
  );

  /** LSC cells inside AddRequestedResource FormProvider (saved with the row). */
  const logisticsEditCells = (index: number, item?: ResourceRequestItem) => (
    <RowLogisticsEditCells
      index={index}
      item={item}
      request={request}
      editable={(isLogs && !logsDisabled) || logisticsColsEditable}
      costEditable={costEditable}
    />
  );

  const logisticsCellsForRow = (index: number, item: ResourceRequestItem) => {
    if (logisticsColsEditable && onLogisticsColsPatch) {
      return (
        <StandaloneLogisticsColsEditor
          index={index}
          item={item}
          onCommit={(patch) => onLogisticsColsPatch(index, patch)}
        />
      );
    }
    return logisticsViewCells(item);
  };

  return (
    <div
      className="tw:flex tw:flex-col tw:gap-3"
      data-testid="ics213rr-form-body"
    >
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel="1. Incident Name:"
          testId="ics213rr-box-1"
          className="tw:mt-0"
        >
          <PrintedValue value={incidentName} testId="ics213rr-incident-name" />
        </Ics204CardSection>
        <Ics204CardSection
          boxLabel="2. Date/Time:"
          testId="ics213rr-box-2"
          className="tw:mt-0"
          {...sectionActivateProps('dateTime')}
        >
          {sectionBody(
            'dateTime',
            headerEditable ? (
              <Input
                type="datetime-local"
                aria-label="Date/Time"
                {...register('timestamp')}
              />
            ) : (
              <PrintedValue value={dateTime} testId="ics213rr-date-time" />
            )
          )}
        </Ics204CardSection>
        <Ics204CardSection
          boxLabel="3. Resource Request Number:"
          testId="ics213rr-box-3"
          className="tw:mt-0"
          {...sectionActivateProps('requestNumber')}
        >
          {sectionBody(
            'requestNumber',
            headerEditable ? (
              <Input
                aria-label="Resource Request Number"
                placeholder="Assigned on save"
                {...register('request_number')}
              />
            ) : (
              <PrintedValue
                value={request?.request_number ?? ''}
                testId="ics213rr-request-number"
              />
            )
          )}
        </Ics204CardSection>
      </div>

      <FormBand label="Requestor">
        <Ics204CardSection
          boxLabel="4. Order: (Use additional forms when requesting different resource sources of supply.)"
          testId="ics213rr-box-4"
          className="tw:mt-0"
        >
          {addItemSlot}
          <div className="tw:overflow-x-auto">
            <table className="tw:w-full tw:min-w-[64rem] tw:text-left tw:text-sm">
              <thead>
                <tr className="tw:text-muted-foreground">
                  <th className="tw:min-w-48 tw:px-1 tw:py-1 tw:font-medium">
                    Attach
                  </th>
                  <th className="tw:min-w-20 tw:px-1 tw:py-1 tw:font-medium">
                    a. Qty
                    <RequiredMark />
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    b. Kind
                    <RequiredMark />
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    c. Type
                    <RequiredMark />
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    d. Priority
                  </th>
                  <th className="tw:min-w-64 tw:px-1 tw:py-1 tw:font-medium">
                    e. Detailed Item Description
                    <RequiredMark />
                  </th>
                  <th className="tw:min-w-[32rem] tw:px-1 tw:py-1 tw:font-medium">
                    f. Requested Reporting Location
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    f. Date/Time
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    g. Order # (LSC)
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    h. ETA (LSC)
                  </th>
                  <th className="tw:px-1 tw:py-1 tw:font-medium">
                    i. Cost (LSC)
                  </th>
                  {showRowActions ? (
                    <th className="tw:px-1 tw:py-1 tw:font-medium"> </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {addingItem && renderItemEditor
                  ? renderItemEditor({
                      editIndex: null,
                      logisticsCells: costEditable ? (
                        logisticsEditCells(items.length)
                      ) : (
                        <>
                          <td className="tw:px-1 tw:py-1">{NULL_VALUE}</td>
                          <td className="tw:px-1 tw:py-1">{NULL_VALUE}</td>
                          <td className="tw:px-1 tw:py-1">{NULL_VALUE}</td>
                        </>
                      ),
                    })
                  : null}
                {items.length === 0 && !addingItem ? (
                  <tr>
                    <td
                      colSpan={showRowActions ? 12 : 11}
                      className="tw:px-1 tw:py-2 tw:text-muted-foreground"
                    >
                      No line items yet.
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => {
                    const canAttach = !isCreate && !!setLogisticsItems;

                    if (editingItemIndex === index && renderItemEditor) {
                      return (
                        <Fragment key={item.id ?? index}>
                          {renderItemEditor({
                            item,
                            editIndex: index,
                            logisticsCells: logisticsEditCells(index, item),
                          })}
                        </Fragment>
                      );
                    }

                    const orderCells = (
                      <>
                        <td className="tw:min-w-20 tw:px-1 tw:py-1">
                          {item.quantity || NULL_VALUE}
                        </td>
                        <td className="tw:px-1 tw:py-1">
                          {item.kind || NULL_VALUE}
                        </td>
                        <td className="tw:px-1 tw:py-1">
                          {item.type || NULL_VALUE}
                        </td>
                        <td className="tw:px-1 tw:py-1">
                          {normalize213rrPriority(item.priority) || NULL_VALUE}
                        </td>
                        <td className="tw:min-w-64 tw:px-1 tw:py-1 tw:font-medium tw:text-white">
                          {itemDescription(item) || NULL_VALUE}
                        </td>
                        <td className="tw:min-w-[32rem] tw:px-1 tw:py-1">
                          {item.requested_reporting_location || NULL_VALUE}
                        </td>
                        <td className="tw:px-1 tw:py-1">
                          {item.requested_reporting_datetime
                            ? dayjs(item.requested_reporting_datetime).format(
                                'MM/DD/YYYY HH:mm'
                              )
                            : NULL_VALUE}
                        </td>
                        {logisticsCellsForRow(index, item)}
                        {showRowActions && setItems ? (
                          <td className="tw:px-1 tw:py-1 tw:text-right">
                            <div className="tw:flex tw:justify-end tw:gap-1">
                              {onEditItem ? (
                                <Button
                                  type="button"
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label="Edit item"
                                  onClick={() => onEditItem(index)}
                                >
                                  <Pencil className="tw:size-4" />
                                </Button>
                              ) : null}
                              {!canRemoveItem || canRemoveItem(item) ? (
                                <Button
                                  type="button"
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label="Remove item"
                                  onClick={() => {
                                    setItems((prev) =>
                                      prev.filter((_, i) => i !== index)
                                    );
                                    setLogisticsItems?.((prev) =>
                                      prev.filter((_, i) => i !== index)
                                    );
                                  }}
                                >
                                  <Trash2 className="tw:size-4" />
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </>
                    );

                    if (!canAttach || !setLogisticsItems) {
                      return (
                        <tr key={item.id ?? index} className="tw:align-top">
                          <td className="tw:min-w-48 tw:px-1 tw:py-1 tw:text-muted-foreground">
                            {renderAttachCell?.(item, index) ?? NULL_VALUE}
                          </td>
                          {orderCells}
                        </tr>
                      );
                    }

                    return (
                      <CustomResourcesDropdown
                        key={item.id ?? index}
                        arrIndex={index}
                        logisticsItems={logisticsItems[index] || []}
                        setLogisticsItems={setLogisticsItems}
                        reqCount={reqCount}
                        isDisabled={laterBoxesLocked}
                        canTransfer={canTransfer}
                        requestCreatorUserGuid={request?.user_guid}
                        requestorPosition={request?.requestor_position}
                        lockedResourceSetIds={
                          item.locked_attach_resource_set_id
                            ? [item.locked_attach_resource_set_id]
                            : undefined
                        }
                      >
                        {({ picker }) => (
                          <tr className="tw:align-top">
                            <td className="tw:min-w-48 tw:px-1 tw:py-1">
                              {picker}
                            </td>
                            {orderCells}
                          </tr>
                        )}
                      </CustomResourcesDropdown>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel="5. Suggested Source(s) of Supply and Suitable Substitutes:"
          testId="ics213rr-box-5"
          className="tw:mt-0"
          {...sectionActivateProps('supplySources')}
        >
          {sectionBody(
            'supplySources',
            isCreate || editing ? (
              <FormTextAreaField fieldName="supply_sources" />
            ) : (
              <PrintedValue
                value={request?.supply_sources ?? ''}
                testId="ics213rr-supply-sources"
              />
            )
          )}
        </Ics204CardSection>

        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
          <Ics204CardSection
            boxLabel="6. Requested by:"
            testId="ics213rr-box-6"
            className="tw:mt-0"
          >
            <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-3">
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">Name</div>
                <PrintedValue
                  value={request?.requestor_name ?? ''}
                  testId="ics213rr-requestor-name"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Position
                </div>
                <PrintedValue
                  value={request?.requestor_position ?? ''}
                  testId="ics213rr-requestor-position"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Date/Time
                </div>
                <PrintedValue
                  value={
                    request?.requestor_signature_timestamp
                      ? dayjs(request.requestor_signature_timestamp).format(
                          'MM/DD/YYYY HH:mm'
                        )
                      : ''
                  }
                  testId="ics213rr-requestor-datetime"
                />
              </div>
            </div>
            {slotActions('requestedBy')}
          </Ics204CardSection>
          <Ics204CardSection
            boxLabel="7. Section Chief / Command Staff Approval:"
            testId="ics213rr-box-7"
            className="tw:mt-0"
          >
            <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">Name</div>
                <PrintedValue
                  value={request?.section_chief_name ?? ''}
                  testId="ics213rr-section-chief-name"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Position
                </div>
                <PrintedValue
                  value={request?.section_chief_position ?? ''}
                  testId="ics213rr-section-chief-position"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Date/Time
                </div>
                <PrintedValue
                  value={
                    request?.section_chief_signature_timestamp
                      ? dayjs(request.section_chief_signature_timestamp).format(
                          'MM/DD/YYYY HH:mm'
                        )
                      : ''
                  }
                  testId="ics213rr-section-chief-datetime"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Comments
                </div>
                <PrintedValue
                  value={request?.section_chief_comments ?? ''}
                  testId="ics213rr-section-chief-comments"
                />
              </div>
            </div>
            {slotActions('sectionChief')}
          </Ics204CardSection>
        </div>
      </FormBand>

      <FormBand label="Plans">
        <Ics204CardSection
          boxLabel="8. RESL – Check Box (a) if request is for tactical resources or personnel. Then note availability in Box (b) or (c)."
          testId="ics213rr-box-8"
          className={cn(
            'tw:mt-0',
            isCreate && sectionDisabled(reslDisabled)
          )}
          {...sectionActivateProps('reslCheckboxes')}
        >
          {sectionBody(
            'reslCheckboxes',
            isCreate ? (
              <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-5">
                <FormCheckboxField
                  title="a. Tactical"
                  fieldName="resl_tac"
                  disabled={reslDisabled}
                />
                <FormCheckboxField
                  title="a. Personnel"
                  fieldName="resl_personnel"
                  disabled={reslDisabled}
                />
                <FormCheckboxField
                  title="b. Available"
                  fieldName="resl_available"
                  disabled={reslDisabled}
                />
                <FormCheckboxField
                  title="c. Not available"
                  fieldName="resl_not_available"
                  disabled={reslDisabled}
                />
              </div>
            ) : (
              <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-5">
                <div>
                  <div className="tw:text-xs tw:text-muted-foreground">
                    a. Tactical
                  </div>
                  <PrintedValue
                    value={yesNo(request?.resl_tac)}
                    testId="ics213rr-resl-tac"
                  />
                </div>
                <div>
                  <div className="tw:text-xs tw:text-muted-foreground">
                    a. Personnel
                  </div>
                  <PrintedValue
                    value={yesNo(request?.resl_personnel)}
                    testId="ics213rr-resl-personnel"
                  />
                </div>
                <div>
                  <div className="tw:text-xs tw:text-muted-foreground">
                    b. Available
                  </div>
                  <PrintedValue
                    value={yesNo(request?.resl_available)}
                    testId="ics213rr-resl-available"
                  />
                </div>
                <div>
                  <div className="tw:text-xs tw:text-muted-foreground">
                    c. Not available
                  </div>
                  <PrintedValue
                    value={yesNo(request?.resl_not_available)}
                    testId="ics213rr-resl-not-available"
                  />
                </div>
              </div>
            )
          )}
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel="9. RESL Review/Signature:"
          testId="ics213rr-box-9"
          className="tw:mt-0"
        >
          <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-3">
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">Name</div>
              <PrintedValue
                value={request?.resl_name ?? ''}
                testId="ics213rr-resl-name"
              />
            </div>
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">
                Date/Time
              </div>
              <PrintedValue
                value={
                  request?.resl_signature_timestamp
                    ? dayjs(request.resl_signature_timestamp).format(
                        'MM/DD/YYYY HH:mm'
                      )
                    : ''
                }
                testId="ics213rr-resl-datetime"
              />
            </div>
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">
                Comments
              </div>
              <PrintedValue
                value={request?.resl_comments ?? ''}
                testId="ics213rr-resl-comments"
              />
            </div>
          </div>
          {slotActions('reslReview')}
        </Ics204CardSection>
      </FormBand>

      <FormBand label="Logistics">
        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
          <Ics204CardSection
            boxLabel="10. Requisition/Purchase Order #:"
            testId="ics213rr-box-10"
            className={cn(
              'tw:mt-0',
              isCreate && sectionDisabled(logsDisabled)
            )}
            {...sectionActivateProps('logisticsOrder')}
          >
            {sectionBody(
              'logisticsOrder',
              isCreate ? (
                <FormTextField
                  title="Requisition / Purchase Order #"
                  fieldName="logistics_order_number"
                  disabled={logsDisabled}
                />
              ) : (
                <PrintedValue
                  value={request?.logistics_order_number ?? ''}
                  testId="ics213rr-logistics-order-number"
                />
              )
            )}
          </Ics204CardSection>
          <Ics204CardSection
            boxLabel="11. Supplier (Name/Phone/Fax/Email):"
            testId="ics213rr-box-11"
            className={cn(
              'tw:mt-0',
              isCreate && sectionDisabled(logsDisabled)
            )}
            {...sectionActivateProps('supplier')}
          >
            {sectionBody(
              'supplier',
              isCreate ? (
                <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
                  <FormTextField
                    title="Name"
                    fieldName="supplier_name"
                    disabled={logsDisabled}
                  />
                  <FormTextField
                    title="Phone"
                    fieldName="supplier_phone"
                    disabled={logsDisabled}
                  />
                  <FormTextField
                    title="Fax"
                    fieldName="supplier_fax"
                    disabled={logsDisabled}
                  />
                  <FormTextField
                    title="Email"
                    fieldName="supplier_email"
                    disabled={logsDisabled}
                  />
                </div>
              ) : (
                <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
                  <div>
                    <div className="tw:text-xs tw:text-muted-foreground">
                      Name
                    </div>
                    <PrintedValue
                      value={request?.supplier_name ?? ''}
                      testId="ics213rr-supplier-name"
                    />
                  </div>
                  <div>
                    <div className="tw:text-xs tw:text-muted-foreground">
                      Phone
                    </div>
                    <PrintedValue
                      value={request?.supplier_phone ?? ''}
                      testId="ics213rr-supplier-phone"
                    />
                  </div>
                  <div>
                    <div className="tw:text-xs tw:text-muted-foreground">
                      Fax
                    </div>
                    <PrintedValue
                      value={request?.supplier_fax ?? ''}
                      testId="ics213rr-supplier-fax"
                    />
                  </div>
                  <div>
                    <div className="tw:text-xs tw:text-muted-foreground">
                      Email
                    </div>
                    <PrintedValue
                      value={request?.supplier_email ?? ''}
                      testId="ics213rr-supplier-email"
                    />
                  </div>
                </div>
              )
            )}
          </Ics204CardSection>
        </div>

        <Ics204CardSection
          boxLabel="12. Notes:"
          testId="ics213rr-box-12"
          className={cn('tw:mt-0', isCreate && sectionDisabled(logsDisabled))}
          {...sectionActivateProps('logisticsNotes')}
        >
          {sectionBody(
            'logisticsNotes',
            isCreate ? (
              <FormTextAreaField
                title="Notes"
                fieldName="logistics_notes"
                disabled={logsDisabled}
              />
            ) : (
              <PrintedValue
                value={request?.logistics_notes ?? ''}
                testId="ics213rr-logistics-notes"
              />
            )
          )}
        </Ics204CardSection>

        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
          <Ics204CardSection
            boxLabel="13. Logistics Section Approval:"
            testId="ics213rr-box-13"
            className="tw:mt-0"
          >
            <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">Name</div>
                <PrintedValue
                  value={request?.logistics_name ?? ''}
                  testId="ics213rr-logistics-name"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Position
                </div>
                <PrintedValue
                  value={request?.logistics_position ?? ''}
                  testId="ics213rr-logistics-position"
                />
              </div>
              <div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Date/Time
                </div>
                <PrintedValue
                  value={
                    request?.logistics_signature_timestamp
                      ? dayjs(request.logistics_signature_timestamp).format(
                          'MM/DD/YYYY HH:mm'
                        )
                      : ''
                  }
                  testId="ics213rr-logistics-datetime"
                />
              </div>
            </div>
            {slotActions('logisticsApproval')}
          </Ics204CardSection>
          <Ics204CardSection
            boxLabel="14. Order placed by:"
            testId="ics213rr-box-14"
            className={cn(
              'tw:mt-0',
              isCreate && sectionDisabled(logsDisabled)
            )}
            {...sectionActivateProps('orderPlacedBy')}
          >
            {sectionBody(
              'orderPlacedBy',
              isCreate ? (
                <>
                  <FormRadioField
                    title="Order placed by"
                    fieldName="logistics_order_placed_by"
                    options={['SPUL', 'PROC', 'Other']}
                    disabled={logsDisabled}
                    className="tw:flex tw:justify-between tw:pe-2"
                  />
                  <FormTextField
                    title="Other"
                    fieldName="logistics_order_placed_by_other"
                    disabled={logsDisabled}
                  />
                </>
              ) : (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <PrintedValue
                    value={request?.logistics_order_placed_by ?? ''}
                    testId="ics213rr-order-placed-by"
                  />
                  {request?.logistics_order_placed_by_other ? (
                    <PrintedValue
                      value={request.logistics_order_placed_by_other}
                      testId="ics213rr-order-placed-by-other"
                    />
                  ) : null}
                </div>
              )
            )}
          </Ics204CardSection>
        </div>
      </FormBand>

      <FormBand label="Finance">
        <Ics204CardSection
          boxLabel="15. Reply / Comments from Finance:"
          testId="ics213rr-box-15"
          className={cn(
            'tw:mt-0',
            isCreate && sectionDisabled(financeDisabled)
          )}
          {...sectionActivateProps('financeComments')}
        >
          {sectionBody(
            'financeComments',
            isCreate ? (
              <FormTextAreaField
                title="Reply / Comments from Finance"
                fieldName="finance_comments"
                disabled={financeDisabled}
              />
            ) : (
              <PrintedValue
                value={request?.finance_comments ?? ''}
                testId="ics213rr-finance-comments"
              />
            )
          )}
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel="16. Finance Section Approval:"
          testId="ics213rr-box-16"
          className="tw:mt-0"
        >
          <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-3">
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">Name</div>
              <PrintedValue
                value={request?.finance_name ?? ''}
                testId="ics213rr-finance-name"
              />
            </div>
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">
                Position
              </div>
              <PrintedValue
                value={request?.finance_position ?? ''}
                testId="ics213rr-finance-position"
              />
            </div>
            <div>
              <div className="tw:text-xs tw:text-muted-foreground">
                Date/Time
              </div>
              <PrintedValue
                value={
                  request?.finance_signature_timestamp
                    ? dayjs(request.finance_signature_timestamp).format(
                        'MM/DD/YYYY HH:mm'
                      )
                    : ''
                }
                testId="ics213rr-finance-datetime"
              />
            </div>
          </div>
          {slotActions('financeApproval')}
        </Ics204CardSection>
      </FormBand>
    </div>
  );
}
